import { AxiosRequestConfigMethods } from "@/api/types";
import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import { EnvironmentVariables } from "@/modules/common/types";
import {
  CheckMultiplierResponse,
  GetAgentTargetResponse,
  GetOrganizationTargetResponse,
  NewHireTargetResponse,
  TargetMultiplierResponse,
} from "@/modules/targets/types";
import { MoverTargetWithProrationResponse } from "@/modules/user/types/index.v2";
import { SalesResponseData } from "@/modules/sales/dtos";
import { AxiosResponse } from "axios";
import { TargetsAggregateInterface } from "../dtos";
import {
  CommonSalesDetailsSummary,
  QueryTargetAggregate,
  SaleTypeName,
} from "../types";
import { UUID } from "crypto";

export const getSalesDetailsSummary = async ({
  signal,
  query,
}: {
  signal?: AbortSignal;
  query: {
    startDate: string;
    endDate: string;
    organizationUId: string;
    personalOnly?: boolean;
  };
}): Promise<AxiosResponse<CommonSalesDetailsSummary>> => {
  const apiUrl = brandUrl<CommonSalesDetailsSummary>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/sales/sales-details-summary`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: "GET",
      params: {
        ...query,
      },
      signal,
    },
  });
};

export const getSalesDetails = async ({
  signal,
  query,
}: {
  signal?: AbortSignal;
  query: {
    startDate: string;
    endDate: string;
    personalOnly: boolean;
    saleType: SaleTypeName;
    page: number;
    pageSize: number;
    leadName?: string;
    organizationUId: string;
    sortBy?: string;
    sortOrder?: "ASC" | "DESC";
  };
}): Promise<AxiosResponse<SalesResponseData>> => {
  const apiUrl = brandUrl<CommonSalesDetailsSummary>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/sales/sales-details`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: "GET",
      params: query,
      signal,
    },
  });
};

export const getTargetAggregate = async ({
  signal,
  query,
}: {
  signal?: AbortSignal;
  query: QueryTargetAggregate;
}): Promise<AxiosResponse<TargetsAggregateInterface>> => {
  const apiUrl = brandUrl<TargetsAggregateInterface>(
    `${EnvironmentVariables.MIDAS_API}/targets/aggregate`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: "GET",
      params: {
        ...query,
      },
      signal,
    },
  });
};

export const getOrganizationTargets = async (params: {
  from: Date;
  to: Date;
  organizationUIds: (string | undefined)[];
}) => {
  const apiUrl = brandUrl<GetOrganizationTargetResponse>(
    `${EnvironmentVariables.MIDAS_API}/organizationTargets`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<GetOrganizationTargetResponse>;
};

export const getAgentsTargets = async (params: {
  from: Date;
  to: Date;
  organizationUIds: (string | undefined)[];
  subdepartmentUId?: UUID;
}) => {
  const apiUrl = brandUrl<GetAgentTargetResponse>(
    `${EnvironmentVariables.MIDAS_API}/defaulttargets`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<GetAgentTargetResponse>;
};

export const checkTargetMultiplier = async (params: {
  subdepartmentUId: string;
  organizationUId: string;
  rankUId: string;
  percentage: number;
  from: Date;
  to: Date;
}) => {
  const apiUrl = brandUrl<CheckMultiplierResponse>(
    `${EnvironmentVariables.MIDAS_API}/targetmultiplier/checktargetmultiplier`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<CheckMultiplierResponse>;
};

export const createAgentTarget = async (
  data: {
    subDepartmentUId?: string;
    organizationUIds: string[];
    rankUId: string;
    targetType: string;
    targetPeriods: {
      from: Date;
      to: Date;
      amount: number;
    }[];
  }[]
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.MIDAS_API}/defaulttargets`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: true,
      method: AxiosRequestConfigMethods.POST,
      data: {
        data: data,
      },
    },
  });
};

export const createOrganizationTarget = async (data: {
  organizationUIds: (UUID | undefined)[];
  subDepartmentUId: string | undefined;
  targetType: string;
  targetPeriods: {
    from: Date;
    to: Date;
    amount: number;
  }[];
}) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.MIDAS_API}/organizationTargets`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const createTargetMultiplier = async (data: {
  subDepartmentUId: string;
  organizationUId: string;
  rankUId: string;
  count: number;
  percentage: number;
  from: Date;
  to: Date;
}) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.MIDAS_API}/targetmultiplier`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const getTargetMultipliers = async (params: {
  from: Date;
  to: Date;
  organizationUIds: (string | undefined)[];
}) => {
  const apiUrl = brandUrl<TargetMultiplierResponse>(
    `${EnvironmentVariables.MIDAS_API}/targetmultiplier`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<TargetMultiplierResponse>;
};

export const getNewHireTarget = async (params: {
  prorationDate: Date | null;
  rankUId: string | undefined;
  subdepartmentUId: string | undefined;
  organizationUId: string | undefined;
}) => {
  const apiUrl = brandUrl<NewHireTargetResponse>(
    `${EnvironmentVariables.MIDAS_API}/targets/getnewhireproratedtarget`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<NewHireTargetResponse>;
};

export const getMoverTargetWithProration = async (params: {
  userUId: string | undefined;
  prorationDate: Date;
}) => {
  const apiUrl = brandUrl<MoverTargetWithProrationResponse>(
    `${EnvironmentVariables.MIDAS_API}/targets/getmovertargetwithproration`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<MoverTargetWithProrationResponse>;
};

export const getCurrentTarget = async (params: {
  userUId: string;
  from: Date;
  to: Date;
  organizationUId: string;
}) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.MIDAS_API}/targets/getcurrenttarget`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  });
};

export const getLeaverTargetWithProration = async (params: {
  userUId: string;
  prorationDate: Date;
}) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.MIDAS_API}/targets/getleavertargetwithproration`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  });
};
